import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'

interface MonthPickerProps {
  month: number
  year: number
  onNavigate: (month: number, year: number) => void
  children?: React.ReactNode
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export default function MonthPicker({
  month,
  year,
  onNavigate,
  children,
}: MonthPickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerYear, setPickerYear] = useState(year)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const panelWidth = 280
    setPos({
      top: rect.bottom + 6,
      left: rect.left + rect.width / 2 - panelWidth / 2,
    })
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    setPickerYear(year)
    updatePos()
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return
      setPickerOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [pickerOpen, updatePos, year])

  const canGoForward = true

  const prevMonth = () => {
    const m = month === 1 ? 12 : month - 1
    const y = month === 1 ? year - 1 : year
    onNavigate(m, y)
  }

  const nextMonth = () => {
    if (!canGoForward) return
    const m = month === 12 ? 1 : month + 1
    const y = month === 12 ? year + 1 : year
    onNavigate(m, y)
  }

  const selectMonth = (m: number) => {
    setPickerOpen(false)
    onNavigate(m, pickerYear)
  }

  const goToToday = () => {
    const now = new Date()
    setPickerOpen(false)
    onNavigate(now.getMonth() + 1, now.getFullYear())
  }

  const isMonthDisabled = (_m: number): boolean => {
    return false
  }

  const isCurrent = (m: number): boolean => m === month && pickerYear === year

  const today = new Date()
  const isCurrentMonth = month === today.getMonth() + 1 && year === today.getFullYear()

  return (
    <div className="flex items-center justify-between rounded-xl border border-border/60 bg-white px-5 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        {/* Prev arrow */}
        <button
          type="button"
          onClick={prevMonth}
          aria-label="Previous month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-white transition-all hover:bg-muted/80 text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Month/year label — clickable to open picker */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setPickerOpen(!pickerOpen)}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 transition-all',
            pickerOpen
              ? 'bg-primary/5 border border-primary/30 text-primary'
              : 'hover:bg-muted/50 text-foreground'
          )}
        >
          <span className="text-lg font-bold tracking-tight">
            {MONTH_NAMES[month - 1]}
          </span>
          <span className="text-lg font-normal text-muted-foreground">{year}</span>
          <CalendarDays className={cn('h-4 w-4 ml-0.5', pickerOpen ? 'text-primary' : 'text-muted-foreground')} />
        </button>

        {/* Next arrow */}
        <button
          type="button"
          onClick={nextMonth}
          disabled={!canGoForward}
          aria-label="Next month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-white transition-all hover:bg-muted/80 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Filter children */}
      {children && (
        <div className="flex items-center gap-2 flex-wrap">
          {children}
        </div>
      )}

      {/* Month/year picker dropdown */}
      {pickerOpen && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] w-[280px] rounded-lg border border-border/60 bg-white p-3 shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          {/* Year selector */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setPickerYear((y) => y - 1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="text-sm font-bold text-foreground">{pickerYear}</span>
            <button
              type="button"
              onClick={() => setPickerYear((y) => y + 1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Month grid 4×3 */}
          <div className="grid grid-cols-3 gap-1.5">
            {MONTH_SHORT.map((name, i) => {
              const m = i + 1
              const disabled = isMonthDisabled(m)
              const current = isCurrent(m)
              const isThisMonth = m === today.getMonth() + 1 && pickerYear === today.getFullYear()

              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  onClick={() => selectMonth(m)}
                  className={cn(
                    'rounded-md px-2 py-2 text-xs font-medium transition-all',
                    current
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : disabled
                        ? 'text-muted-foreground/40 cursor-not-allowed'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                    isThisMonth && !current && 'ring-1 ring-primary/30'
                  )}
                >
                  {name}
                </button>
              )
            })}
          </div>

          {/* Today button */}
          {!isCurrentMonth && (
            <div className="mt-3 border-t border-border/40 pt-3">
              <button
                type="button"
                onClick={goToToday}
                className="w-full rounded-md px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5"
              >
                Go to Today
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
